/**
 * Shared plumbing for the MSW v2 handlers: problem+json responses, the
 * list-envelope helper backed by the keyset paginator, the in-memory
 * Idempotency-Key store (replay + 409 `platform.idempotency.key_reuse`), and the
 * in-memory ETag registry for If-Match / 412 flows.
 *
 * NOTE: all endpoint paths use the SPECs' ASSUMED `/api/v1` base path,
 * pending `/openapi/erp.yaml` (CODEX-1 toolchain). Handlers register with a
 * leading `*` so they match any origin under both browser and node (tests).
 */
import { HttpResponse } from "msw";
import { CursorError, paginate } from "../helpers/paginate";
import {
  PROBLEM_CONTENT_TYPE,
  catalogPreconditionFailed,
  makeProblem,
  preconditionFailed,
  idempotencyReuse,
  validationProblem,
  type Problem,
} from "../helpers/problem";

/** Prefix a spec path with the ASSUMED base path and an any-origin wildcard. */
export const api = (path: string): string => `*/api/v1${path}`;

/** Serialize an RFC 9457 problem with the right content type. */
export function problemResponse(problem: Problem): Response {
  return HttpResponse.json(problem, {
    status: problem.status,
    headers: { "Content-Type": PROBLEM_CONTENT_TYPE },
  });
}

/**
 * Paginate `items` from the request's `?cursor=&limit=` into the
 * `{ items, next_cursor }` envelope; malformed/stale cursors → 400 problem.
 */
export function listResponse<T>(
  request: Request,
  items: readonly T[],
  key: (item: T) => string,
): Response {
  const url = new URL(request.url);
  const cursor = url.searchParams.get("cursor");
  const limitRaw = url.searchParams.get("limit");
  const limit = limitRaw === null ? null : Number(limitRaw);
  try {
    return HttpResponse.json(
      paginate(items, key, { cursor, limit }) as unknown as Record<string, unknown>,
    );
  } catch (error) {
    if (error instanceof CursorError) {
      return problemResponse(makeProblem("cursor_invalid", 400, "Invalid cursor", error.message));
    }
    throw error;
  }
}

// ── Idempotency-Key store ────────────────────────────────────────────────

interface StoredResponse {
  fingerprint: string;
  status: number;
  /** Exact JSON bytes captured before caller-owned state can mutate. */
  body: string;
  /** Exact successful response headers, including Content-Type and ETag. */
  headers: Record<string, string>;
}

const idempotencyStore = new Map<string, StoredResponse>();

export type IdempotentResult =
  | {
      status?: number;
      body: Record<string, unknown>;
      /** Resource id whose current ETag is attached to the response (ratified creates require it). */
      etagFor?: string;
      /** `Location` header for 201/202 creates that declare one. */
      location?: string;
    }
  | { problem: Problem };

/** Body bytes captured before a handler parses a non-JSON request. */
export interface PreparedIdempotentBody {
  rawBody: ArrayBuffer;
  payload: Record<string, unknown>;
}

function canonicalContentType(value: string | null): string {
  if (value === null) return "";
  const [mediaType = "", ...rawParameters] = value.split(";");
  const parameters = rawParameters
    .map((parameter) => parameter.trim())
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));
  return [mediaType.trim().toLowerCase(), ...parameters].join("; ");
}

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) throw new Error("Web Crypto SHA-256 is required by mock idempotency");
  const digest = new Uint8Array(await subtle.digest("SHA-256", bytes));
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Wrap a mutating POST per SPEC-CONVENTIONS idempotency semantics:
 * - missing Idempotency-Key → 400 `idempotency_key_missing`
 * - same key + same payload → replay of the original result
 *   (headers `Idempotency-Key` echo + `Idempotency-Replayed: true`)
 * - same key + different exact request bytes/Content-Type → 409
 *   `platform.idempotency.key_reuse`
 * Problems returned by `build` are NOT stored (only successes replay).
 */
export async function idempotentPost(
  request: Request,
  build: (payload: Record<string, unknown>) => IdempotentResult,
  prepared?: PreparedIdempotentBody,
): Promise<Response> {
  const key = request.headers.get("Idempotency-Key");
  if (!key) {
    return problemResponse(
      makeProblem(
        "idempotency_key_missing",
        400,
        "Missing Idempotency-Key",
        "Every mutating POST must send an Idempotency-Key header (client UUIDv7 per logical action).",
      ),
    );
  }
  let text = "";
  let rawBody: ArrayBuffer;
  let payload: Record<string, unknown>;
  if (prepared === undefined) {
    text = await request.text();
    rawBody = new TextEncoder().encode(text).buffer;
    payload = {};
    if (text.trim() !== "") {
      try {
        payload = JSON.parse(text) as Record<string, unknown>;
      } catch {
        return problemResponse(validationProblem([], "Request body is not valid JSON."));
      }
    }
  } else {
    rawBody = prepared.rawBody;
    payload = prepared.payload;
  }
  const url = new URL(request.url);
  const fingerprint = [
    request.method,
    `${url.pathname}${url.search}`,
    canonicalContentType(request.headers.get("Content-Type")),
    await sha256Hex(rawBody),
  ].join(" ");

  const existing = idempotencyStore.get(key);
  if (existing) {
    if (existing.fingerprint !== fingerprint) {
      return problemResponse(idempotencyReuse());
    }
    return new HttpResponse(existing.body, {
      status: existing.status,
      headers: {
        ...existing.headers,
        "Idempotency-Replayed": "true",
      },
    });
  }

  const result = build(payload);
  if ("problem" in result) {
    return problemResponse(result.problem);
  }
  const status = result.status ?? 200;
  const responseBody = JSON.stringify(result.body);
  const responseHeaders = {
    "Content-Type": "application/json",
    "Idempotency-Key": key,
    ...(result.etagFor !== undefined ? { ETag: currentEtag(result.etagFor) } : {}),
    ...(result.location !== undefined ? { Location: result.location } : {}),
  };
  idempotencyStore.set(key, {
    fingerprint,
    status,
    body: responseBody,
    headers: responseHeaders,
  });
  return new HttpResponse(responseBody, {
    status,
    headers: responseHeaders,
  });
}

// ── ETag / If-Match registry ─────────────────────────────────────────────

const versions = new Map<string, number>();

/**
 * Current strong version-derived ETag for a resource id (every record starts
 * at version 1). The ratified contracts mandate strong quoted validators —
 * weak `W/` tags are invalid; the canonical contract pins the `"v<n>"` spelling.
 */
export function currentEtag(resourceId: string): string {
  return `"v${versions.get(resourceId) ?? 1}"`;
}

/** Attach the resource's ETag to a JSON 200 response. */
export function jsonWithEtag(resourceId: string, body: unknown): Response {
  return HttpResponse.json(body as Record<string, unknown>, {
    headers: { ETag: currentEtag(resourceId) },
  });
}

/**
 * Guard a PUT/PATCH with If-Match. Returns a 412 problem response when the
 * header is missing or stale. By default, success bumps the stored version
 * (so a replayed If-Match goes stale, mimicking optimistic concurrency).
 * Callers with domain checks after the precondition may defer that bump and
 * invoke {@link bumpEtag} only once the mutation is known to commit.
 */
export function ifMatchGuard(
  request: Request,
  resourceId: string,
  options: { catalogProblem?: boolean; deferVersionBump?: boolean } = {},
): Response | null {
  const provided = request.headers.get("If-Match");
  if (!provided || provided !== currentEtag(resourceId)) {
    return problemResponse(
      options.catalogProblem
        ? catalogPreconditionFailed()
        : preconditionFailed(currentEtag(resourceId)),
    );
  }
  if (options.deferVersionBump !== true) bumpEtag(resourceId);
  return null;
}

/** Complete a previously validated deferred If-Match mutation. */
export function bumpEtag(resourceId: string): void {
  versions.set(resourceId, (versions.get(resourceId) ?? 1) + 1);
}

// ── Stateful-handler reset registry ──────────────────────────────────────

const resetCallbacks: Array<() => void> = [];

/**
 * Register a callback that re-seeds a handler module's mutable in-memory
 * state (product imports, count worksheets, partner overrides, …). Called by
 * {@link resetMockState} so tests/stories keep their single reset entrypoint.
 */
export function onResetMockState(callback: () => void): void {
  resetCallbacks.push(callback);
}

/** Reset idempotency + ETag + stateful-handler state between tests / story reloads. */
export function resetMockState(): void {
  idempotencyStore.clear();
  versions.clear();
  for (const callback of resetCallbacks) callback();
}
